import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import { db } from "@/lib/firebase";

type Stats = {
  total: number;
  sale: number;
  trade: number;
  saleValue: number;
};

export function useUserCollectionStats(uid?: string | null): Stats | null {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!uid) {
      setStats(null);
      return;
    }
    (async () => {
      try {
        const base = collection(db, "users", uid, "cards");

        // Accurate counts via server aggregation
        const [totalSnap, saleSnap, tradeSnap] = await Promise.all([
          getCountFromServer(query(base)),
          getCountFromServer(query(base, where("for_sale", "==", true))),
          getCountFromServer(query(base, where("for_trade", "==", true))),
        ]);

        // Sum sale value (price * qty) across all for_sale docs
        // Try paginating by createdAt; if not available, fall back to one-shot (<=1000)
        let sum = 0;
        let last: QueryDocumentSnapshot<DocumentData> | null = null;
        let paginatedWorked = true;

        for (let page = 0; page < 50; page++) {
          let qy;
          try {
            qy = last
              ? query(base, where("for_sale", "==", true), orderBy("createdAt", "desc"), startAfter(last), limit(500))
              : query(base, where("for_sale", "==", true), orderBy("createdAt", "desc"), limit(500));
          } catch {
            paginatedWorked = false;
            break;
          }
          const snap = await getDocs(qy);
          if (snap.empty) break;
          for (const d of snap.docs) {
            const v = d.data() as any;
            const price = typeof v?.price === "number" ? v.price : 0;
            const qty = Number.isFinite(v?.qty) ? Number(v.qty) : 1;
            sum += price * qty;
          }
          last = snap.docs[snap.docs.length - 1];
          if (snap.size < 500) break;
        }

        if (!paginatedWorked) {
          // Fallback: one-shot up to 1000 docs without ordering
          const snap = await getDocs(query(base, where("for_sale", "==", true), limit(1000)));
          sum = 0;
          for (const d of snap.docs) {
            const v = d.data() as any;
            const price = typeof v?.price === "number" ? v.price : 0;
            const qty = Number.isFinite(v?.qty) ? Number(v.qty) : 1;
            sum += price * qty;
          }
        }

        if (!cancelled) {
          setStats({
            total: totalSnap.data().count,
            sale: saleSnap.data().count,
            trade: tradeSnap.data().count,
            saleValue: sum,
          });
        }
      } catch {
        if (!cancelled) setStats(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  return stats;
}
